import { unstable_cache as serverCache } from 'next/cache';
import { Locale } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, ProductsRepositoryType } from '../../../../DI';
import { ProductType } from '../../../../client';
import { ProductDetailValue, ProductDetailVariantValue, TileProductValue, VipBrandsValue } from '../../../../server';

const PRODUCT_RECONCILIATION_MEDIA_REVALIDATION_TIME_SECONDS = 30 * 60; // 30 minutes

const getProductVariants = async (
    productVariants: ProductDetailVariantValue[],
    locale: Locale,
    vipBrands: VipBrandsValue,
): Promise<Record<string, TileProductValue> | undefined> => {
    const variantsIds = productVariants.map((variant) => variant.id);
    const variants = await CommonDI.get(ProductsRepositoryType).getProducts(locale, variantsIds, vipBrands);

    return unwrapResultSuccess(variants)?.reduce((acc, current) => {
        return { ...acc, [current.id]: current };
    }, {});
};

/**
 * Reconciles product media by fetching and updating variant images.
 *
 * @param {Locale} locale - The locale for the request.
 * @param {ProductDetailValue} productResult - The product details to be reconciled.
 * @param {VipBrandsValue} vipBrands - VIP brands information.
 * @returns {Promise<void>} A promise that resolves when the reconciliation is complete.
 */
export const overwriteProductMedia = serverCache(
    async (locale: Locale, productDetail: ProductDetailValue, vipBrands: VipBrandsValue): Promise<void> => {
        // Combine all variants (from product sets and standard products) into a single array
        const allVariants =
            productDetail.type === ProductType.PRODUCT_SET
                ? productDetail.productsSetList?.flatMap((setItem) => setItem.variants) || []
                : productDetail.variants;

        if (allVariants.length === 0) {
            return; // No variants to process
        }

        // Fetch and map variant images
        const variantsMap = await getProductVariants(allVariants, locale, vipBrands);

        // Update variant images
        allVariants.forEach((variant) => {
            const variantImage = variantsMap?.[variant.id]?.image;
            if (variantImage) {
                variant.image = variantImage;
            }
        });
    },
    undefined,
    { revalidate: PRODUCT_RECONCILIATION_MEDIA_REVALIDATION_TIME_SECONDS },
);
